import { useDroppable } from '@dnd-kit/core';
import { ActionIcon, Avatar, Tooltip } from '@mantine/core';
import { User, Clock, Edit2 } from 'react-feather';
import type { GroupWithMembers } from '../../../services/api/groups';
import type { Participant, OverallHealthStatus } from '../../../types';
import { formatGroupSchedule } from './groups/groupUtils';
import { GroupTitleTooltip } from './GroupTitleTooltip';
import { GroupBookingLinks } from '../../coach/cohort-detail/GroupBookingLinks';
import { getAvatarColor, getInitialsFromParts } from '../../../utils/avatarHelpers';
import classes from './GroupAssignmentCard.module.css';

interface GroupAssignmentCardProps {
  group: GroupWithMembers;
  cohortId: string;
  participantMap: Record<string, Participant>;
  sessionCount: number;
  isSelectionMode: boolean;
  isSelected: boolean;
  isAvailableForSelection: boolean;
  selectedCoachName?: string;
  coachConflicts?: Array<{ date: string; reason?: string }>;
  /** Whether this card is selected in group-first mode */
  isGroupFirstSelected?: boolean;
  /** MED-394: Health status for group */
  healthStatus?: OverallHealthStatus;
  /** When a participant is selected, false = dim this card (no time slot match) */
  isParticipantFiltered?: boolean;
  onToggleSelection: () => void;
  /** Handler for group-first selection toggle */
  onGroupFirstToggle?: () => void;
  onRemoveCoach: () => void;
  onViewConflicts: () => void;
  /** MED-1294 (AC2): open the rename modal. Card view's half of the affordance. */
  onRenameGroup?: (group: GroupWithMembers) => void;
  /** MED-1353: show the staff "Booking links" button, as the List view card does. */
  showBookingLinks?: boolean;
}

export function GroupAssignmentCard({
  group,
  participantMap,
  sessionCount,
  isSelectionMode,
  isSelected,
  isAvailableForSelection,
  selectedCoachName,
  coachConflicts,
  isGroupFirstSelected,
  healthStatus,
  isParticipantFiltered,
  onToggleSelection,
  onGroupFirstToggle,
  onRemoveCoach,
  onViewConflicts,
  onRenameGroup,
  showBookingLinks = false,
}: GroupAssignmentCardProps) {
  const isAssigned = Boolean(group.coach_id);
  const schedule = formatGroupSchedule(group);

  const { isOver, setNodeRef } = useDroppable({
    id: `coach-drop-group-${group.id}`,
    data: { group },
  });

  const isDisabled = (isSelectionMode && !isAvailableForSelection) || isParticipantFiltered === false;

  const isEffectivelySelected = isSelected || isGroupFirstSelected;

  const cardClass = [
    classes.card,
    isOver && !isDisabled ? classes.cardDragOver : '',
    isEffectivelySelected ? classes.cardSelected : '',
    isDisabled ? classes.cardDisabled : '',
    (isSelectionMode && isAvailableForSelection) ? classes.cardClickable : '',
    (!isAssigned && !isSelectionMode) ? classes.cardClickable : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleCardClick = () => {
    // Coach-first flow: toggle group selection for the selected coach
    if (isSelectionMode && isAvailableForSelection) {
      onToggleSelection();
      return;
    }
    // Group-first flow: toggle group selection when no coach is selected
    if (!isSelectionMode && !isAssigned && onGroupFirstToggle) {
      onGroupFirstToggle();
    }
  };

  // Coach avatar
  const coachAvatarColor = group.coach_id
    ? getAvatarColor(group.coach_id)
    : undefined;
  const coachInitials = group.coach_name
    ? getInitialsFromParts(
        group.coach_name.split(' ')[0] || null,
        group.coach_name.split(' ').slice(1).join(' ') || null,
      )
    : '?';

  return (
    <div
      ref={setNodeRef}
      className={cardClass}
      onClick={handleCardClick}
    >
      {/* Header */}
      <div className={classes.headerRow}>
        <div className={classes.groupIcon}>
          <User size={16} color="#4A7C59" />
        </div>
        <div className={classes.headerInfo}>
          <GroupTitleTooltip group={group} participantMap={participantMap}>
            <span className={classes.groupName}>
              {group.name}
              {healthStatus && (
                <span style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: healthStatus === 'at_risk' ? '#D64545' : healthStatus === 'monitor' ? '#E9A23B' : healthStatus === 'no_data' ? '#ccc' : '#4A7C59',
                  display: 'inline-block',
                  flexShrink: 0,
                  marginLeft: 6,
                  verticalAlign: 'middle',
                }} />
              )}
            </span>
          </GroupTitleTooltip>
          <div className={classes.subtitle}>
            {group.member_count} participant{group.member_count !== 1 ? 's' : ''}
            {sessionCount > 0 && ` \u00B7 ${sessionCount} session${sessionCount !== 1 ? 's' : ''}`}
          </div>
        </div>
        {/* MED-1294 (AC1/AC2): rename entry for the card view. headerInfo is
            flex:1, so this sits at the right edge of the header. The whole card
            has an onClick for coach/group selection and the name is wrapped in
            GroupTitleTooltip, so this is a distinct control with
            stopPropagation rather than a clickable name. */}
        {onRenameGroup && (
          <Tooltip label="Rename group">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label={`Rename ${group.name}`}
              onClick={(e) => {
                e.stopPropagation();
                onRenameGroup(group);
              }}
            >
              <Edit2 size={13} />
            </ActionIcon>
          </Tooltip>
        )}
      </div>

      {/* Pills */}
      <div className={classes.pillsRow}>
        {schedule && <span className={classes.schedulePill}>{schedule}</span>}
        <span className={isAssigned ? classes.statusPillAssigned : classes.statusPillUnassigned}>
          {isAssigned ? 'Assigned' : 'Unassigned'}
        </span>
        {showBookingLinks && (
          <GroupBookingLinks
            groupId={group.id}
            groupName={group.name}
            viewer="staff"
            hasCoach={isAssigned}
          />
        )}
      </div>

      {/* Assigned state: Coach row */}
      {isAssigned && group.coach_name && (
        <div className={classes.coachRow}>
          <Avatar
            size={28}
            radius="xl"
            src={group.coach_avatar_url || undefined}
            styles={{
              root: { backgroundColor: coachAvatarColor },
              placeholder: { color: '#FFFFFF', backgroundColor: coachAvatarColor },
            }}
          >
            {coachInitials}
          </Avatar>
          <div className={classes.coachInfo}>
            <div className={classes.coachName}>{group.coach_name}</div>
            {sessionCount > 0 && (
              <div className={classes.coachSessions}>
                {sessionCount} session{sessionCount !== 1 ? 's' : ''} assigned
              </div>
            )}
          </div>
          <button
            type="button"
            className={classes.removeButton}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveCoach();
            }}
          >
            Remove
          </button>
        </div>
      )}

      {/* Conflict warning — shown for both assigned and unassigned groups during matching */}
      {coachConflicts && coachConflicts.length > 0 && (
        <div className={classes.conflictRow}>
          <Clock size={12} color="#E9A23B" />
          <span className={classes.conflictText}>
            {coachConflicts.length} conflict{coachConflicts.length !== 1 ? 's' : ''}
          </span>
          <button
            type="button"
            className={classes.conflictViewButton}
            onClick={(e) => {
              e.stopPropagation();
              onViewConflicts();
            }}
          >
            View
          </button>
        </div>
      )}

      {/* Unassigned state: Drop zone */}
      {!isAssigned && (
        <div className={`${classes.dropZone} ${isOver ? classes.dropZoneActive : ''} ${isGroupFirstSelected ? classes.dropZoneGroupFirst : ''}`}>
          <div className={classes.dropZoneText}>
            <span className={classes.dropZonePlus}>+</span>
            <span>
              {isSelectionMode && selectedCoachName
                ? `Click to assign ${selectedCoachName}`
                : isGroupFirstSelected
                  ? 'Select a coach to assign'
                  : 'Drop a coach here to assign'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
